import type { LiveUpdateTokenResponse } from "@/logic/api";
import { io, type Socket } from "socket.io-client";
import {
  createLiveUpdateTokenSource,
  LiveUpdateTokenConfigurationError,
} from "./liveUpdateToken";
import {
  parseLiveUpdateMessage,
  type LiveUpdateEvent,
} from "./liveUpdateMessage";
import type { LiveUpdateFailureCategory } from "./diagnostics";

export const LIVE_UPDATE_EVENT_NAME = "live-update";
export const LIVE_UPDATE_NAMESPACE = "/portal";

export const LIVE_UPDATE_TRANSPORT_STATES = [
  "connecting", "connected", "delayed", "unavailable",
] as const;

export type TransportState = (typeof LIVE_UPDATE_TRANSPORT_STATES)[number];

export interface TransportStateChange {
  state: TransportState;
  failureCategory: LiveUpdateFailureCategory | null;
  // For diagnostics, absent from relayed or faked states
  retryAttempt?: number;
  tokenExpiresAt?: number | null;
}

export interface TransportConsumer {
  onEvent(event: LiveUpdateEvent): void;
  onReconnect(): void;
  onStateChange(change: TransportStateChange): void;
  onMalformedMessage(): void;
}

export interface LiveUpdateTransport {
  start(consumer: TransportConsumer): void;
  stop(): void;
  retry(): void;
  forceDisconnect(): void;
  reconnect(): void;
  dispose(): void;
}

interface SocketIoTransportOptions {
  requestToken(signal: AbortSignal): Promise<LiveUpdateTokenResponse>;
  diagnosticsEnabled?: boolean;
  random?: () => number;
}

const MAX_RECONNECT_DELAY_MS = 30_000;
export const LIVE_UPDATE_AUTHENTICATION_ERROR_CODE =
  "LIVE_UPDATE_AUTHENTICATION_FAILED";

const isAuthenticationError = (error: Error): boolean => {
  const code = (error as Error & { data?: { code?: unknown } }).data?.code;
  if (code === LIVE_UPDATE_AUTHENTICATION_ERROR_CODE) return true;
  const message = error.message.toLowerCase();
  return (
    message.includes("unauthor") ||
    message.includes("authentication") ||
    message.includes("invalid token") ||
    message.includes("jwt")
  );
};

/** it owns the complete socket.io and live-token lifecycle */
export const createSocketIoTransport = ({
  requestToken,
  diagnosticsEnabled = false,
  random = Math.random,
}: SocketIoTransportOptions): LiveUpdateTransport => {
  let socket: Socket | null = null;
  let consumer: TransportConsumer | null = null;
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  // Invalidates async token results whenever this transport is stopped/restarted
  let generation = 0;
  let retryAttempt = 0;
  let hasConnected = false;
  let authenticationRefreshUsed = false;
  let manuallyDisconnected = false;
  let disposed = false;

  const clearReconnectTimer = (): void => {
    if (reconnectTimer) clearTimeout(reconnectTimer);
    reconnectTimer = null;
  };

  const detachSocket = (): void => {
    if (!socket) return;
    socket.removeAllListeners();
    socket.io.removeAllListeners();
    socket.disconnect();
    socket = null;
  };

  const closeConnection = (): void => {
    // Also invalidate token requests that have not created a socket yet.
    generation += 1;
    clearReconnectTimer();
    detachSocket();
  };

  const tokenSource = createLiveUpdateTokenSource({
    requestToken,
    onRenewalDue: () => {
      if (!consumer || disposed || manuallyDisconnected) return;
      reconnect(true);
    },
  });

  const publish = (
    state: TransportState,
    failureCategory: LiveUpdateFailureCategory | null = null,
  ): void =>
    consumer?.onStateChange({
      state,
      failureCategory,
      ...(diagnosticsEnabled
        ? {
          retryAttempt,
          tokenExpiresAt: tokenSource.getExpiresAt(),
        }
        : {}),
    });

  const scheduleConnect = (forceTokenRefresh = false): void => {
    if (
      disposed ||
      !consumer ||
      manuallyDisconnected ||
      reconnectTimer ||
      socket?.connected
    ) {
      return;
    }

    const ceiling = Math.min(MAX_RECONNECT_DELAY_MS, 1_000 * 2 ** retryAttempt);
    const delay = retryAttempt === 0 ? 0 : Math.floor(random() * ceiling);
    retryAttempt += 1;
    reconnectTimer = setTimeout(() => {
      reconnectTimer = null;
      void connect(forceTokenRefresh);
    }, delay);
  };

  const handleConnectionFailure = (
    error: Error,
    currentGeneration: number,
  ): void => {
    if (disposed || !consumer || currentGeneration !== generation) return;

    if (isAuthenticationError(error)) {
      if (!authenticationRefreshUsed) {
        authenticationRefreshUsed = true;
        tokenSource.clear();
        publish("delayed", "authentication");
        scheduleConnect(true);
        return;
      }

      tokenSource.clear();
      publish("unavailable", "authentication");
      return;
    }

    publish("delayed", "network");
    scheduleConnect();
  };

  const attachSocket = (
    { token, socketUrl }: LiveUpdateTokenResponse,
    currentGeneration: number,
  ): void => {
    if (disposed || !consumer || currentGeneration !== generation) return;

    const url = new URL(socketUrl);
    // socket.io treats the URL path as a namespace; its HTTP endpoint goes in `path`
    const nextSocket = io(`${url.origin}${LIVE_UPDATE_NAMESPACE}`, {
      path: url.pathname,
      auth: { token },
      autoConnect: false,
      forceNew: true,
      reconnection: false,
    });
    socket = nextSocket;

    nextSocket.on(LIVE_UPDATE_EVENT_NAME, (value: unknown) => {
      const event = parseLiveUpdateMessage(value);
      if (!event) {
        consumer?.onMalformedMessage();
        return;
      }
      consumer?.onEvent(event);
    });
    nextSocket.on("connect", () => {
      const reconnected = hasConnected;
      hasConnected = true;
      retryAttempt = 0;
      authenticationRefreshUsed = false;
      publish("connected");
      if (reconnected) consumer?.onReconnect();
    });
    nextSocket.on("connect_error", (error: Error) => {
      detachSocket();
      handleConnectionFailure(error, currentGeneration);
    });
    nextSocket.on("disconnect", () => {
      if (socket !== nextSocket || disposed || !consumer) return;
      detachSocket();
      publish("delayed", "network");
      scheduleConnect();
    });
    nextSocket.connect();
  };

  const connect = async (forceTokenRefresh = false): Promise<void> => {
    if (
      disposed ||
      !consumer ||
      manuallyDisconnected ||
      socket?.connected ||
      socket?.active
    ) {
      return;
    }

    const currentGeneration = generation;
    publish("connecting");

    try {
      const token = await tokenSource.getToken(forceTokenRefresh);
      attachSocket(token, currentGeneration);
    } catch (error) {
      if (disposed || !consumer || currentGeneration !== generation) return;
      if (error instanceof LiveUpdateTokenConfigurationError) {
        publish("unavailable", "configuration");
        return;
      }
      publish("delayed", "token");
      scheduleConnect();
    }
  };

  const reconnect = (forceTokenRefresh = false): void => {
    if (disposed || !consumer) return;
    closeConnection();
    manuallyDisconnected = false;
    scheduleConnect(forceTokenRefresh);
  };

  return {
    start: (nextConsumer) => {
      if (disposed) return;
      consumer = nextConsumer;
      manuallyDisconnected = false;
      scheduleConnect();
    },
    stop: () => {
      closeConnection();
      tokenSource.clear();
      consumer = null;
      retryAttempt = 0;
      authenticationRefreshUsed = false;
      manuallyDisconnected = false;
      hasConnected = false;
    },
    retry: () => {
      if (!consumer || disposed) return;
      retryAttempt = 0;
      authenticationRefreshUsed = false;
      tokenSource.clear();
      reconnect(true);
    },
    forceDisconnect: () => {
      if (!consumer || disposed) return;
      closeConnection();
      manuallyDisconnected = true;
      tokenSource.clear();
      publish("delayed", "network");
    },
    reconnect: () => reconnect(false),
    dispose: () => {
      if (disposed) return;
      disposed = true;
      closeConnection();
      consumer = null;
      tokenSource.dispose();
    },
  };
};
